import api from './api';
import { Tarefa } from '../types';

// Interface para os filtros de listagem de tarefas admin
export interface TarefaAdminFiltros {
  status?: string;
  prioridade?: string;
  responsavel?: string;
  criador?: string;
  departamento?: string;
  pagina?: number;
  limite?: number;
  arquivadas?: boolean;
  dataInicio?: string;
  dataFim?: string;
  carregarConcluidas?: boolean;
  diasConcluidas?: number;
  texto?: string; // Filtro de texto para busca em título ou descrição
}

// Interface para a resposta paginada
export interface RespostaPaginada<T> {
  success: boolean;
  tarefas: T[];
  total: number;
  pagina: number;
  totalPaginas: number;
}

// Interface para estatísticas globais
export interface EstatisticasGlobais {
  total_tarefas: number;
  tarefas_ativas: number;
  tarefas_concluidas: number;
  tarefas_arquivadas: number;
  tarefas_alta_prioridade: number;
  tarefas_atrasadas: number;
}

// Interface para estatísticas por departamento
export interface EstatisticaDepartamento {
  id: string;
  nome: string;
  total_tarefas: number;
  tarefas_ativas: number;
  tarefas_concluidas: number;
}

// Interface para estatísticas por usuário
export interface EstatisticaUsuario {
  id: string;
  nome: string;
  email: string;
  tarefas_ativas: number;
  tarefas_concluidas: number;
}

// Interface para resposta de estatísticas
export interface RespostaEstatisticas {
  success: boolean;
  estatisticas: EstatisticasGlobais;
  departamentos: EstatisticaDepartamento[];
  usuarios: EstatisticaUsuario[];
}

export const tarefaAdminService = {
  /**
   * Busca todas as tarefas do sistema com filtros opcionais e suporte a paginação
   * Acesso restrito a administradores
   */
  listarTarefasGlobais: async (filtros?: TarefaAdminFiltros) => {
    try {
      const params = new URLSearchParams();
      if (filtros) {
        Object.entries(filtros).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
      }
      
      // Adicionar timestamp para evitar cache do navegador
      params.append('_t', Date.now().toString());
      
      const response = await api.get(`/tarefas-admin?${params.toString()}`);
      return response.data as RespostaPaginada<Tarefa>;
    } catch (error) {
      console.error('Erro ao listar tarefas globais:', error);
      throw error;
    }
  },
  
  /**
   * Busca tarefas arquivadas globais (histórico)
   */
  listarTarefasArquivadasGlobais: async (filtros?: Omit<TarefaAdminFiltros, 'arquivadas'>) => {
    try {
      return await tarefaAdminService.listarTarefasGlobais({
        ...filtros,
        arquivadas: true,
        pagina: filtros?.pagina || 1,
        limite: filtros?.limite || 20
      });
    } catch (error) {
      console.error('Erro ao listar tarefas arquivadas globais:', error);
      throw error;
    }
  },

  /**
   * Busca tarefas concluídas globais (histórico)
   */
  listarTarefasConcluidasGlobais: async (filtros?: TarefaAdminFiltros) => {
    try {
      return await tarefaAdminService.listarTarefasGlobais({
        ...filtros,
        status: 'Concluída',
        pagina: filtros?.pagina || 1,
        limite: filtros?.limite || 20,
        carregarConcluidas: true,
        // Por padrão, carregamos tarefas concluídas nos últimos 30 dias
        diasConcluidas: filtros?.diasConcluidas || 30
      });
    } catch (error) {
      console.error('Erro ao listar tarefas concluídas globais:', error);
      throw error;
    }
  },
  
  /**
   * Obtém estatísticas globais do sistema
   */
  obterEstatisticasGlobais: async () => {
    try {
      const response = await api.get('/tarefas-admin/estatisticas');
      return response.data as RespostaEstatisticas;
    } catch (error) {
      console.error('Erro ao obter estatísticas globais:', error);
      throw error;
    }
  },
  
  /**
   * Obtém uma tarefa específica usando a visão de administrador
   * Esta rota permite que administradores visualizem qualquer tarefa do sistema
   * independentemente de permissões normais
   */
  obterTarefa: async (id: string) => {
    try {
      const response = await api.get(`/tarefas-admin/${id}`);
      return response.data.tarefa;
    } catch (error) {
      console.error(`Erro ao obter tarefa ${id} (visão admin):`, error);
      throw error;
    }
  }
};

export default tarefaAdminService;
